// src/components/MemberForm.jsx

import React, { useState, useEffect } from 'react';
import { doc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { firestore, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './MemberForm.css';
import Spinner from '../Common/Spinner'; // Import Spinner component

const MemberForm = ({ initialData, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    apellido: '',
    apt: '',
    ciudad: '',
    codigoPostal: '',
    correo: '',
    direccion: '',
    estado: '',
    estadoCivil: '',
    fechaNacimiento: '',
    grupo: '',
    medioTabita: '',
    nombre: '',
    parroquia: '',
    participoRetiro: '',
    profesion: '',
    sacramentos: [],
    sendSMS: true,
    telefono: '',
    photoUrl: null,
    dateCreated: null,
    dateUpdated: null,
    // Visibility states
    personalInfoVisible: true,
    contactInfoVisible: true,
    additionalInfoVisible: true,
  });

  const [photoLoading, setPhotoLoading] = useState(false); // New state for photo loading

  useEffect(() => {
    if (initialData) {
      // Handle fechaNacimiento
      let formattedFechaNacimiento = '';
      if (initialData.fechaNacimiento) {
        if (initialData.fechaNacimiento.seconds) {
          // Firestore Timestamp
          formattedFechaNacimiento = new Date(
            initialData.fechaNacimiento.seconds * 1000
          )
            .toISOString()
            .substr(0, 10);
        } else if (initialData.fechaNacimiento.toDate) {
          // Firestore Timestamp with toDate method
          formattedFechaNacimiento = initialData.fechaNacimiento
            .toDate()
            .toISOString()
            .substr(0, 10);
        } else if (initialData.fechaNacimiento instanceof Date) {
          // JavaScript Date object
          formattedFechaNacimiento = initialData.fechaNacimiento
            .toISOString()
            .substr(0, 10);
        } else if (typeof initialData.fechaNacimiento === 'string') {
          // Date string
          formattedFechaNacimiento = initialData.fechaNacimiento;
        }
      }

      // Handle sacramentos
      const formattedSacramentos = initialData.sacramentos
        ? initialData.sacramentos.map((sacramento) =>
            decodeURIComponent(sacramento.trim())
          )
        : [];

      setFormData({
        apellido: initialData.apellido || '',
        apt: initialData.apt || '',
        ciudad: initialData.ciudad || '',
        codigoPostal: initialData.codigoPostal || '',
        correo: initialData.correo || '',
        direccion: initialData.direccion || '',
        estado: initialData.estado || '',
        estadoCivil: initialData.estadoCivil || '',
        fechaNacimiento: formattedFechaNacimiento,
        grupo: initialData.grupo || '',
        medioTabita: initialData.medioTabita || '',
        nombre: initialData.nombre || '',
        parroquia: initialData.parroquia || '',
        participoRetiro: initialData.participoRetiro || '',
        profesion: initialData.profesion || '',
        sacramentos: formattedSacramentos,
        sendSMS: initialData.sendSMS !== undefined ? initialData.sendSMS : true,
        telefono: initialData.telefono || '',
        photoUrl: initialData.photoUrl || null,
        dateCreated: initialData.dateCreated || null,
        dateUpdated: initialData.dateUpdated || null,
        // Visibility states
        personalInfoVisible: true,
        contactInfoVisible: true,
        additionalInfoVisible: true,
      });
    } else {
      // If no initialData, reset the form
      setFormData({
        apellido: '',
        apt: '',
        ciudad: '',
        codigoPostal: '',
        correo: '',
        direccion: '',
        estado: '',
        estadoCivil: '',
        fechaNacimiento: '',
        grupo: '',
        medioTabita: '',
        nombre: '',
        parroquia: '',
        participoRetiro: '',
        profesion: '',
        sacramentos: [],
        sendSMS: true,
        telefono: '',
        photoUrl: null,
        dateCreated: null,
        dateUpdated: null,
        // Visibility states
        personalInfoVisible: true,
        contactInfoVisible: true,
        additionalInfoVisible: true,
      });
    }
  }, [initialData]);

  // Effect to handle photo loading state when photoUrl changes
  useEffect(() => {
    if (formData.photoUrl) {
      setPhotoLoading(true);
    } else {
      setPhotoLoading(false);
    }
  }, [formData.photoUrl]);

  const handlePhotoChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setPhotoLoading(true); // Start loading
      try {
        const storageRef = ref(storage, `photos/${file.name}-${Date.now()}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        setFormData({ ...formData, photoUrl: downloadURL });
        // setPhotoLoading(false); // Will be set in onLoad of img
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Error al subir la foto. Por favor, inténtelo de nuevo.');
        setPhotoLoading(false); // Stop loading on error
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'selectAllSacramentos') {
        if (checked) {
          setFormData({
            ...formData,
            sacramentos: ['Bautismo', 'Primera Comunión', 'Confirmación'],
          });
        } else {
          setFormData({
            ...formData,
            sacramentos: [],
          });
        }
      } else if (name === 'sacramentos') {
        if (checked) {
          setFormData({
            ...formData,
            sacramentos: [...formData.sacramentos, value],
          });
        } else {
          setFormData({
            ...formData,
            sacramentos: formData.sacramentos.filter(
              (item) => item !== value
            ),
          });
        }
      } else {
        setFormData({
          ...formData,
          [name]: checked,
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateAndFormatPhone = (phone) => {
    let cleanedPhone = phone.replace(/[\s\-\(\)\.]/g, '');
    if (cleanedPhone.length === 10) {
      cleanedPhone = `+1${cleanedPhone}`;
    } else if (cleanedPhone.length === 11 && cleanedPhone.startsWith('1')) {
      cleanedPhone = `+${cleanedPhone}`;
    } else if (cleanedPhone.length === 12 && cleanedPhone.startsWith('+1')) {
      cleanedPhone = cleanedPhone;
    } else {
      return null;
    }
    return cleanedPhone;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedPhone = validateAndFormatPhone(formData.telefono);
    if (!formattedPhone) {
      alert(
        'Número de teléfono no válido. Asegúrese de que tenga el formato correcto.'
      );
      return;
    }

    try {
      let docId = formattedPhone;
      if (initialData?.id) {
        // If editing, use the existing document ID
        docId = initialData.id;
      }

      const docRef = doc(firestore, 'records', docId);
      const now = Timestamp.now();

      const dataToSave = {
        ...formData,
        telefono: formattedPhone,
        fechaNacimiento: formData.fechaNacimiento
          ? Timestamp.fromDate(
              new Date(`${formData.fechaNacimiento}T00:00:00`)
            )
          : null,
        sacramentos: formData.sacramentos.map((sacramento) =>
          encodeURIComponent(sacramento.trim())
        ),
        dateCreated: formData.dateCreated || now,
        dateUpdated: now,
      };

      if (initialData?.id) {
        await updateDoc(docRef, dataToSave);
        onUpdate({ id: initialData.id, ...dataToSave });
      } else {
        await setDoc(docRef, dataToSave);
        onUpdate({ id: docId, ...dataToSave });
      }

      onClose();
    } catch (e) {
      console.error('Error adding/updating document: ', e);
      alert('Error al guardar la información. Por favor, inténtelo de nuevo.');
    }
  };

  return (
    <div className="member-form-container">
      <form className="member-form-form" onSubmit={handleSubmit}>
        {/* Photo Section */}
        <div className="member-form-section member-form-photo-section">
          <div className="member-form-photo-selector">
            <input
              type="file"
              id="photoUrl"
              name="photoUrl"
              accept="image/*"
              onChange={handlePhotoChange}
              className="member-form-photo-input"
            />
            <label htmlFor="photoUrl" className="member-form-photo-label">
              <div className="member-form-photo-icon">
                {photoLoading && (
                  <div className="image-spinner">
                    <Spinner size="50px" color="#bb8300" fullScreen={false} />
                  </div>
                )}
                <img
                  src={
                    formData.photoUrl ||
                    'https://firebasestorage.googleapis.com/v0/b/tabitapp-6b25a.appspot.com/o/PhotoIcon.jpg?alt=media&token=b25447cd-09f1-4083-9e0a-75e600874c5b'
                  }
                  alt="Agregar Foto"
                  onLoad={() => setPhotoLoading(false)}
                  onError={() => setPhotoLoading(false)}
                />
              </div>
              <span className="member-form-photo-text">Agregar Foto</span>
            </label>
          </div>
        </div>

        {/* Personal Information Section */}
        <div
          className={`member-form-section ${
            formData.personalInfoVisible ? 'fade-in' : 'fade-out'
          }`}
        >
          <h3>Información Personal</h3>
          <div className="member-form-row">
            <div className="member-form-input-group">
              <label htmlFor="nombre" className="member-form-label-required">
                Nombre
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Nombre"
                required
              />
            </div>

            <div className="member-form-input-group">
              <label htmlFor="apellido" className="member-form-label-required">
                Apellido
              </label>
              <input
                type="text"
                id="apellido"
                name="apellido"
                value={formData.apellido}
                onChange={handleChange}
                placeholder="Apellido"
                required
              />
            </div>
          </div>

          <div className="member-form-row">
            <div className="member-form-input-group">
              <label htmlFor="telefono" className="member-form-label-required">
                Teléfono
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="Teléfono"
                required
              />
            </div>

            <div className="member-form-input-group">
              <label htmlFor="fechaNacimiento">Fecha de Nacimiento</label>
              <input
                type="date"
                id="fechaNacimiento"
                name="fechaNacimiento"
                value={formData.fechaNacimiento}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="member-form-input-group">
            <label htmlFor="estadoCivil">Estado Civil</label>
            <select
              id="estadoCivil"
              name="estadoCivil"
              value={formData.estadoCivil}
              onChange={handleChange}
            >
              <option value="">Seleccione Estado Civil</option>
              <option value="Soltera">Soltera</option>
              <option value="Casada">Casada</option>
              <option value="Viuda">Viuda</option>
              <option value="Divorciada">Divorciada</option>
              <option value="Unión Libre">Unión Libre</option>
            </select>
          </div>
        </div>

        {/* Contact Information Section */}
        <div
          className={`member-form-section ${
            formData.contactInfoVisible ? 'fade-in' : 'fade-out'
          }`}
        >
          <h3>Contacto</h3>
          <div className="member-form-row">
            <div className="member-form-input-group">
              <label htmlFor="correo">Correo Electrónico</label>
              <input
                type="email"
                id="correo"
                name="correo"
                value={formData.correo}
                onChange={handleChange}
                placeholder="Correo Electrónico"
              />
            </div>

            <div className="member-form-input-group">
              <label htmlFor="direccion">Dirección</label>
              <input
                type="text"
                id="direccion"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Dirección"
              />
            </div>
          </div>

          <div className="member-form-row">
            <div className="member-form-input-group">
              <label htmlFor="apt">Apt</label>
              <input
                type="text"
                id="apt"
                name="apt"
                value={formData.apt}
                onChange={handleChange}
                placeholder="Apt"
              />
            </div>

            <div className="member-form-input-group">
              <label htmlFor="ciudad">Ciudad</label>
              <input
                type="text"
                id="ciudad"
                name="ciudad"
                value={formData.ciudad}
                onChange={handleChange}
                placeholder="Ciudad"
              />
            </div>
          </div>

          <div className="member-form-row">
            <div className="member-form-input-group">
              <label htmlFor="estado">Estado</label>
              <select
                id="estado"
                name="estado"
                value={formData.estado}
                onChange={handleChange}
              >
                <option value="">Seleccione Estado</option>
                <option value="New York">New York</option>
                <option value="Connecticut">Connecticut</option>
                <option value="New Jersey">New Jersey</option>
                <option value="Other">Otro</option>
              </select>
            </div>

            <div className="member-form-input-group">
              <label htmlFor="codigoPostal">Código Postal</label>
              <input
                type="text"
                id="codigoPostal"
                name="codigoPostal"
                value={formData.codigoPostal}
                onChange={handleChange}
                placeholder="Código Postal"
              />
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div
          className={`member-form-section ${
            formData.additionalInfoVisible ? 'fade-in' : 'fade-out'
          }`}
        >
          <h3>Información Adicional</h3>
          <div className="member-form-input-group">
            <label htmlFor="grupo">
              ¿Perteneces a algún grupo? En caso de que sí, ¿a cuál?
            </label>
            <input
              type="text"
              id="grupo"
              name="grupo"
              value={formData.grupo}
              onChange={handleChange}
              placeholder="Nombre del grupo"
            />
          </div>

          <div className="member-form-input-group">
            <label htmlFor="parroquia">Parroquia</label>
            <input
              type="text"
              id="parroquia"
              name="parroquia"
              value={formData.parroquia}
              onChange={handleChange}
              placeholder="Nombre de la Parroquia"
            />
          </div>

          <div className="member-form-input-group">
            <label htmlFor="medioTabita">
              ¿Por cuál medio te enteraste de Tabita?
            </label>
            <select
              id="medioTabita"
              name="medioTabita"
              value={formData.medioTabita}
              onChange={handleChange}
            >
              <option value="">Seleccione Medio</option>
              <option value="Redes Sociales (Facebook/Instagram)">
                Redes Sociales (Facebook/Instagram)
              </option>
              <option value="Amiga/Conocida">Amiga/Conocida</option>
              <option value="Retiro">Retiro</option>
              <option value="Other">Otro</option>
            </select>
          </div>

          <div className="member-form-input-group">
            <label>Sacramentos</label>
            <div className="member-form-sacramentos-container">
              <div className="member-form-checkbox-item">
                <input
                  type="checkbox"
                  name="selectAllSacramentos"
                  onChange={handleChange}
                  checked={
                    formData.sacramentos.length === 3 &&
                    formData.sacramentos.includes('Bautismo') &&
                    formData.sacramentos.includes('Primera Comunión') &&
                    formData.sacramentos.includes('Confirmación')
                  }
                />
                <label>Seleccionar Todos</label>
              </div>
              <div className="member-form-checkbox-item">
                <input
                  type="checkbox"
                  name="sacramentos"
                  value="Bautismo"
                  checked={formData.sacramentos.includes('Bautismo')}
                  onChange={handleChange}
                />
                <label>Bautismo</label>
              </div>
              <div className="member-form-checkbox-item">
                <input
                  type="checkbox"
                  name="sacramentos"
                  value="Primera Comunión"
                  checked={formData.sacramentos.includes('Primera Comunión')}
                  onChange={handleChange}
                />
                <label>Primera Comunión</label>
              </div>
              <div className="member-form-checkbox-item">
                <input
                  type="checkbox"
                  name="sacramentos"
                  value="Confirmación"
                  checked={formData.sacramentos.includes('Confirmación')}
                  onChange={handleChange}
                />
                <label>Confirmación</label>
              </div>
            </div>
          </div>

          <div className="member-form-input-group">
            <label htmlFor="participoRetiro">
              ¿Has participado de algún retiro anteriormente?
            </label>
            <select
              id="participoRetiro"
              name="participoRetiro"
              value={formData.participoRetiro}
              onChange={handleChange}
            >
              <option value="">Seleccione</option>
              <option value="Si">Sí</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="member-form-input-group">
            <div className="member-form-checkbox-item">
              <input
                type="checkbox"
                id="sendSMS"
                name="sendSMS"
                checked={formData.sendSMS}
                onChange={handleChange}
              />
              <label htmlFor="sendSMS">Enviar Mensaje</label>
            </div>
          </div>
        </div>

        <button type="submit" className="member-form-submit-button">
          Guardar
        </button>
      </form>
    </div>
  );
};

export default MemberForm;
